/**
 * Pointing at things in the room, on hardware that may have nothing to point
 * with.
 *
 * The panels this layer hangs in the room were built against a Quest: a
 * controller reports a pose every frame, so a ray exists continuously and a
 * menu can be hovered before it is pressed. An Apple Vision Pro has none of
 * that. Its WebXR input is `transient-pointer` — an input source that appears
 * **at the instant of a pinch**, carries a ray pointing where the player is
 * looking, and is gone when they let go. Apple deliberately does not hand over
 * continuous gaze, so there is no cursor to poll, no hover, and nothing to
 * highlight before the press. `render/xrintent.ts` is the same observation
 * written as a contract for locomotion; this is the interface half of it.
 *
 * So this file answers two questions and keeps them apart:
 *
 *   `aim`    where a *continuous* ray is pointing, or null. Controllers only.
 *            Drives hover, which is a bonus and never a requirement.
 *   `press`  the ray at the instant the primary action went down, held for one
 *            frame. **This is the one that must always work**, because on a
 *            Vision Pro it is the only ray that will ever exist.
 *
 * The press ray is captured inside the `selectstart` handler rather than read
 * off a pose afterwards, because "afterwards" may be after the input source has
 * gone. It is captured in the session's reference space — the space controller
 * poses arrive in — and lifted into world space in `sample`, against the rig as
 * it stands in the frame the hit test actually runs in.
 *
 * Nothing here is constructed or subscribed until `attach`, which `layer.ts`
 * calls when a session starts and never on a desktop.
 */

import * as THREE from 'three';

/** A ray in world space. Structurally the shape `XRIntent.ray` carries. */
export interface XRRay {
  readonly origin: THREE.Vector3;
  readonly direction: THREE.Vector3;
}

/**
 * What the session turned out to have, which decides what the panels say and
 * how they behave when a press misses.
 *
 * `unknown` until an input source has been seen, and deliberately behaves like
 * `stick` until then: that is the status quo, and a headset with controllers
 * reports them on the first frame anyway.
 */
export type PointerStyle = 'unknown' | 'stick' | 'point';

/** Reused every frame so pointing allocates nothing. */
const _origin = new THREE.Vector3();
const _direction = new THREE.Vector3();
const _pressOrigin = new THREE.Vector3();
const _pressDirection = new THREE.Vector3();
const _world = new THREE.Matrix4();

/**
 * three's XR controller groups are the only continuous rays available, and
 * three only builds two of them for this game (`render/xrhands.ts` asks for
 * indices 0 and 1). Asking for a third would *lengthen* three's own controller
 * array, which is what it uses to decide how many simultaneous input sources it
 * will accept — a hidden change to somebody else's session for no gain here.
 */
const CONTROLLERS = 2;

export class XRPointer {
  /** The continuous ray, or null on a session that has none. */
  aim: XRRay | null = null;
  /** The ray at the moment the primary action went down. One frame only. */
  press: XRRay | null = null;
  /** The primary action is down right now. */
  held = false;
  /** Seconds it has been down, zero on the frame it went down. */
  heldFor = 0;
  /** It came up this frame. */
  released = false;

  private style: PointerStyle = 'unknown';

  private session: XRSession | null = null;
  private renderer: THREE.WebGLRenderer | null = null;

  /**
   * The reference-space pose of the ray that pressed, and whether one is
   * waiting to be consumed. Kept as a matrix rather than as a world ray so the
   * conversion happens against the rig the hit test is about to run against:
   * the event lands at the top of a frame, before locomotion has moved it.
   */
  private readonly pressMatrix = new THREE.Matrix4();
  private pressPending = false;
  /**
   * A release is latched the same way a press is, and for the same reason:
   * both events land between frames, and a flag written directly would be
   * cleared by the next `sample` before anything had a chance to read it.
   */
  private releasePending = false;
  /** Which controller slot pressed last, so two hands do not fight over `aim`. */
  private activeSlot = 0;
  /** How many sources are down. Two triggers on a Quest can both be. */
  private downCount = 0;

  /**
   * True once a real `selectstart` has been seen. Latched: a runtime that fires
   * events is trusted from then on, and `presenceFallback` stops guessing.
   */
  private sawSelectEvent = false;

  /**
   * Start listening. Called on entering a session; safe to call again.
   *
   * The listeners go on the `XRSession` rather than on three's controller
   * groups on purpose. three routes `selectstart` to a controller only once it
   * has assigned that input source one of its two slots, and a transient
   * pointer is created, fired and destroyed inside a single gesture — this way
   * the press is heard whatever three made of the source, and the pose comes
   * from the event's own frame, which is the only frame it can be read in.
   */
  attach(renderer: THREE.WebGLRenderer): void {
    this.renderer = renderer;
    this.rebind();
  }

  /** Stop listening and forget the session. Called when the session ends. */
  detach(): void {
    this.renderer = null;
    this.rebind();
    this.aim = null;
    this.press = null;
    this.held = false;
    this.heldFor = 0;
    this.released = false;
    this.pressPending = false;
    this.releasePending = false;
    this.downCount = 0;
    this.activeSlot = 0;
    this.style = 'unknown';
    this.sawSelectEvent = false;
  }

  /** What this session can do, for the words the panels use. */
  get inputStyle(): PointerStyle {
    return this.style;
  }

  /**
   * One frame of pointing. Call after the scene's world matrices are current,
   * because every ray here is derived from one.
   */
  sample(dt: number): void {
    this.press = null;
    this.released = this.releasePending;
    this.releasePending = false;
    this.rebind();
    const session = this.session;
    const renderer = this.renderer;
    if (!session || !renderer) {
      this.aim = null;
      this.pressPending = false;
      return;
    }

    this.readStyle(session);
    this.presenceFallback(session);

    // The rig: whatever three's controller groups are parented to. That is the
    // space reference-space poses have to be lifted out of, and `render/xr.ts`
    // is the only thing that parents them anywhere, so asking the group is more
    // honest than assuming the camera's parent is the same object.
    const space = renderer.xr.getController(0).parent;
    if (this.pressPending) {
      this.pressPending = false;
      if (space) {
        _world.multiplyMatrices(space.matrixWorld, this.pressMatrix);
        this.press = rayFromMatrix(_world, _pressOrigin, _pressDirection);
      }
    } else if (this.held) {
      // After the press has been consumed, so the frame a press arrives on
      // reads as a fresh press rather than as one already a frame old.
      this.heldFor += dt;
    }

    this.aim = this.readAim(renderer);
  }

  /**
   * A press inferred from a transient pointer merely existing.
   *
   * `render/xrpinch.ts` has this fallback and this file did not, which was the
   * worse half of the same bug: with no select events the pointer still claims
   * the ray — suppressing both interact and travel — while nothing ever opens,
   * so a held pinch on a panel does nothing at all for as long as it is held.
   * A menu that cannot be pressed and also eats the press is the one failure
   * that leaves a player with no way out of a session.
   *
   * A transient pointer exists *only* between a pinch starting and ending, so
   * its presence is the gesture. Restricted to that source kind: a controller
   * is present all the time and would fire continuously.
   */
  private presenceFallback(session: XRSession): void {
    let transient: XRInputSource | null = null;
    for (const source of session.inputSources) {
      if (source.targetRayMode === 'transient-pointer') {
        transient = source;
        break;
      }
    }
    // Only ever a fallback: a runtime that fires events has already set these,
    // and this must not double-count a press it did fire.
    if (transient && !this.held && !this.pressPending && !this.sawSelectEvent) {
      this.pressPending = true;
      this.held = true;
      this.heldFor = 0;
      const frame = this.renderer?.xr.getFrame();
      const refSpace = this.renderer?.xr.getReferenceSpace();
      if (frame && refSpace) {
        const pose = frame.getPose(transient.targetRaySpace, refSpace);
        if (pose) this.pressMatrix.fromArray(pose.transform.matrix);
      }
    } else if (!transient && this.held && !this.sawSelectEvent) {
      this.held = false;
      this.releasePending = true;
      this.heldFor = 0;
    }
  }

  /**
   * The continuous ray, on the hardware that has one.
   *
   * The hand that pressed most recently wins, which is both predictable and
   * self-correcting: before anybody has pressed anything the first tracked hand
   * is used, and one press puts it right for the rest of the session. A
   * cleverer "whichever moved most" rule would swap hands while somebody was
   * reading, which is worse than being wrong until first contact.
   */
  private readAim(renderer: THREE.WebGLRenderer): XRRay | null {
    for (let n = 0; n < CONTROLLERS; n++) {
      const slot = (this.activeSlot + n) % CONTROLLERS;
      const group = renderer.xr.getController(slot);
      // three sets this false unless a pose arrived this frame, and false again
      // the moment the source disconnects. It is the whole liveness test.
      if (!group.visible || !group.parent) continue;
      return rayFromMatrix(group.matrixWorld, _origin, _direction);
    }
    return null;
  }

  /**
   * What kind of session this is, latched.
   *
   * A stick, once seen, is taken as permanent: a controller that goes to sleep
   * mid-run must not turn the menus into a Vision Pro. The reverse latch is not
   * safe the same way, so `point` is only concluded from a source that can
   * never have a stick — a transient pointer, or a tracked hand.
   */
  private readStyle(session: XRSession): void {
    if (this.style === 'stick') return;
    const sources = session.inputSources;
    for (let i = 0; i < sources.length; i++) {
      const source = sources[i];
      if (!source) continue;
      const axes = source.gamepad ? source.gamepad.axes : null;
      if (axes && axes.length >= 4) {
        this.style = 'stick';
        return;
      }
      if (source.targetRayMode === 'transient-pointer' || source.hand) this.style = 'point';
    }
  }

  private rebind(): void {
    const next = this.renderer ? this.renderer.xr.getSession() : null;
    if (next === this.session) return;
    if (this.session) {
      this.session.removeEventListener('selectstart', this.onSelectStart);
      this.session.removeEventListener('selectend', this.onSelectEnd);
    }
    this.session = next;
    if (!next) return;
    next.addEventListener('selectstart', this.onSelectStart);
    next.addEventListener('selectend', this.onSelectEnd);
  }

  private onSelectStart = (event: XRInputSourceEvent): void => {
    // Latched before the early returns: the runtime has spoken whether or not
    // this particular press can be resolved, and that is what the presence
    // fallback needs to know in order to stand down.
    this.sawSelectEvent = true;
    const renderer = this.renderer;
    if (!renderer) return;
    const refSpace = renderer.xr.getReferenceSpace();
    if (!refSpace) return;
    // Read here, from this event's own frame: on a Vision Pro the input source
    // that produced it may be gone by the time anything else runs, and its ray
    // is the only report there will ever be of where the player was looking.
    const pose = event.frame.getPose(event.inputSource.targetRaySpace, refSpace);
    this.downCount++;
    if (!pose) return;
    this.pressMatrix.fromArray(pose.transform.matrix);
    this.pressPending = true;
    this.held = true;
    this.heldFor = 0;
    const slot = this.slotOf(event.inputSource);
    if (slot >= 0) this.activeSlot = slot;
  };

  private onSelectEnd = (): void => {
    // The *last* source up ends the hold, so a slider being dragged with one
    // hand does not stop because the other let go of something else.
    this.downCount = Math.max(0, this.downCount - 1);
    if (this.downCount > 0) return;
    this.held = false;
    this.releasePending = true;
  };

  /**
   * Which of three's two controller slots this source is driving, or -1.
   *
   * three does not publish its source-to-slot map. Its assignment is "first
   * free slot in `session.inputSources` order", so the position in that list is
   * right whenever nothing has disconnected — which is the case that matters,
   * since the only thing this decides is which hand `aim` follows.
   */
  private slotOf(source: XRInputSource): number {
    const sources = this.session ? this.session.inputSources : null;
    if (!sources) return -1;
    for (let i = 0; i < sources.length && i < CONTROLLERS; i++) {
      if (sources[i] === source) return i;
    }
    return -1;
  }
}

/** Origin and -Z of a world matrix, written into the vectors handed in. */
function rayFromMatrix(m: THREE.Matrix4, origin: THREE.Vector3, direction: THREE.Vector3): XRRay {
  const e = m.elements;
  origin.set(e[12]!, e[13]!, e[14]!);
  // A WebXR target ray always runs along -Z of its own space; the spec pins
  // that down, which is why the ray space and not the grip is what anything
  // that has to point hangs off.
  direction.set(-e[8]!, -e[9]!, -e[10]!).normalize();
  return { origin, direction };
}
